import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';

const CustomerAccountSettings = () => {
  const { user, userProfile, refreshProfile } = useAuth();
  const [profile, setProfile] = useState({
    fullName: '',
    phone: '',
    preferredAirport: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  // Major international airports list
  const airports = [
    { value: '', label: 'Select preferred departure airport (optional)' },
    { value: 'LHR', label: 'London Heathrow (LHR)' },
    { value: 'LGW', label: 'London Gatwick (LGW)' },
    { value: 'MAN', label: 'Manchester (MAN)' },
    { value: 'EDI', label: 'Edinburgh (EDI)' },
    { value: 'BHX', label: 'Birmingham (BHX)' },
    { value: 'GLA', label: 'Glasgow (GLA)' },
    { value: 'BRS', label: 'Bristol (BRS)' },
    { value: 'NCL', label: 'Newcastle (NCL)' },
    { value: 'LPL', label: 'Liverpool (LPL)' },
    { value: 'STN', label: 'London Stansted (STN)' },
    { value: 'LTN', label: 'London Luton (LTN)' },
    { value: 'EMA', label: 'East Midlands (EMA)' },
    { value: 'BFS', label: 'Belfast International (BFS)' },
    { value: 'ABZ', label: 'Aberdeen (ABZ)' },
    { value: 'SOU', label: 'Southampton (SOU)' }
  ];

  // Load or create profile on mount
  useEffect(() => {
    const loadOrCreateProfile = async () => {
      if (!user?.id) {
        console.log('[Settings] No user ID, waiting for authentication...');
        setLoading(false);
        return;
      }

      console.log('[Settings] Loading profile for user:', user?.id);
      setLoading(true);
      setError(null);

      try {
        // Try to fetch existing profile
        const { data: existingProfile, error: fetchError } = await supabase?.from('user_profiles')?.select('*')?.eq('id', user?.id)?.single();

        if (fetchError && fetchError?.code !== 'PGRST116') {
          // PGRST116 means "no rows returned" which is expected for new users
          console.error('[Settings] Error fetching profile:', fetchError);
          throw fetchError;
        }

        if (existingProfile) {
          // Profile exists, load it
          console.log('[Settings] Profile found:', existingProfile);
          setProfile({
            fullName: existingProfile?.full_name || '',
            phone: existingProfile?.phone || '',
            preferredAirport: existingProfile?.location || ''
          });
        } else {
          // Profile doesn't exist, create it
          console.log('[Settings] No profile found, creating new profile...');
          
          const newProfileData = {
            id: user?.id,
            email: user?.email,
            full_name: user?.user_metadata?.full_name || user?.email?.split('@')?.[0] || 'Customer',
            phone: '',
            location: '',
            role: 'customer'
          };

          const { data: createdProfile, error: createError } = await supabase?.from('user_profiles')?.insert(newProfileData)?.select()?.single();

          if (createError) {
            console.error('[Settings] Error creating profile:', createError);
            throw createError;
          }

          console.log('[Settings] Profile created successfully:', createdProfile);
          setProfile({
            fullName: createdProfile?.full_name || '',
            phone: createdProfile?.phone || '',
            preferredAirport: createdProfile?.location || ''
          });

          // Refresh auth context to update userProfile
          if (refreshProfile) {
            await refreshProfile();
          }
        }
      } catch (err) {
        console.error('[Settings] Error in loadOrCreateProfile:', err);
        setError({
          message: err?.message || 'Failed to load profile',
          details: err?.details || 'Please try refreshing the page'
        });
      } finally {
        setLoading(false);
      }
    };

    loadOrCreateProfile();
  }, [user?.id, user?.email, user?.user_metadata, refreshProfile]);

  const handleInputChange = (field, value) => {
    setProfile((prev) => ({
      ...prev,
      [field]: value
    }));
    // Clear success message when user starts editing
    if (successMessage) setSuccessMessage('');
  };

  const handleSave = async () => {
    if (!user?.id) {
      setError({ message: 'Not authenticated' });
      return;
    }

    setSaving(true);
    setError(null);
    setSuccessMessage('');

    try {
      console.log('[Settings] Saving profile...');

      const updateData = {
        full_name: profile?.fullName?.trim() || user?.email?.split('@')?.[0] || 'Customer',
        phone: profile?.phone?.trim() || '',
        location: profile?.preferredAirport || ''
      };

      const { data, error: updateError } = await supabase?.from('user_profiles')?.update(updateData)?.eq('id', user?.id)?.select()?.single();

      if (updateError) {
        console.error('[Settings] Error updating profile:', updateError);
        throw updateError;
      }

      console.log('[Settings] Profile updated successfully:', data);
      setSuccessMessage('Profile updated successfully!');

      // Refresh auth context to update userProfile
      if (refreshProfile) {
        await refreshProfile();
      }

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('[Settings] Error saving profile:', err);
      setError({
        message: err?.message || 'Failed to save profile',
        details: err?.details || 'Please try again'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset form to original profile values
    if (userProfile) {
      setProfile({
        fullName: userProfile?.full_name || '',
        phone: userProfile?.phone || '',
        preferredAirport: userProfile?.location || ''
      });
    }
    setSuccessMessage('');
    setError(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header userRole="customer" />
        <div className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading your settings...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header userRole="customer" />
      <main className="pt-16 px-4 lg:px-6 py-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <button
              onClick={() => window.history?.back()}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
              aria-label="Go back"
            >
              <Icon name="ArrowLeft" size={20} className="text-muted-foreground" />
            </button>
            <h1 className="text-2xl font-bold text-foreground">Account Settings</h1>
          </div>
          <p className="text-muted-foreground pl-11">
            Manage your profile information and preferences
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-destructive/10 border border-destructive rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="AlertCircle" size={20} className="text-destructive mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-destructive">{error?.message}</p>
                {error?.details && (
                  <p className="text-sm text-destructive/80 mt-1">{error?.details}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 bg-accent/10 border border-accent rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="CheckCircle" size={20} className="text-accent mt-0.5" />
              <p className="font-medium text-accent">{successMessage}</p>
            </div>
          </div>
        )}

        {/* Profile Form */}
        <div className="bg-card border border-border rounded-lg p-6 space-y-6">
          {/* Account Information Section */}
          <div>
            <h2 className="text-lg font-semibold text-foreground mb-4">
              Profile Information
            </h2>
            
            <div className="space-y-4">
              {/* Email (Read-only) */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Input
                    type="email"
                    value={user?.email || ''}
                    disabled
                    className="bg-muted cursor-not-allowed"
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <Icon name="Lock" size={16} className="text-muted-foreground" />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Email address cannot be changed
                </p>
              </div>

              {/* Full Name */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Full Name *
                </label>
                <Input
                  type="text"
                  value={profile?.fullName}
                  onChange={(e) => handleInputChange('fullName', e?.target?.value)}
                  placeholder="Enter your full name"
                  required
                />
              </div>

              {/* Phone Number */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Phone Number
                </label>
                <Input
                  type="tel"
                  value={profile?.phone}
                  onChange={(e) => handleInputChange('phone', e?.target?.value)}
                  placeholder="+44 7700 900123"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Used for travel agents to contact you
                </p>
              </div>

              {/* Preferred Departure Airport */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Preferred Departure Airport
                </label>
                <Select
                  value={profile?.preferredAirport}
                  onChange={(e) => handleInputChange('preferredAirport', e?.target?.value)}
                  options={airports}
                  className="w-full"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  This helps agents provide more relevant flight options
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleSave}
              disabled={saving || !profile?.fullName?.trim()}
              iconName={saving ? undefined : "Save"}
              iconPosition="left"
              className="bg-accent hover:bg-accent/90 text-accent-foreground"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-accent-foreground border-t-transparent rounded-full animate-spin mr-2"></div>
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </div>

        {/* Additional Information */}
        <div className="mt-6 bg-muted/50 border border-border rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={20} className="text-primary mt-0.5" />
            <div className="flex-1 text-sm text-muted-foreground">
              <p className="font-medium text-foreground mb-1">About Your Profile</p>
              <p>
                Your profile information is used by travel agents to provide personalized
                holiday recommendations. Keep your details up-to-date for the best experience.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CustomerAccountSettings;